import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Button, { TYPES } from './Button';
import { css, StyleSheet } from 'aphrodite/no-important';
import { WHITE, LOCHMARA, MEDIA_QUERIES, BASE_SPACING, ALTO } from '../styles';
import Wrapper from './Wrapper';
import { navigateTo } from '../utils';
import { getActiveTab } from '../redux/app';
import { getTranslation } from './Text';

const s = StyleSheet.create({
  root: {
    width: '100%',
    backgroundColor: WHITE,
    borderBottom: `1px solid ${ALTO}`,
  },
  tab: {
    position: 'relative',
    display: 'flex',
    fontSize: 15,
    padding: '21px 9px',
    ':hover': {
      filter: 'brightness(110%)',
    },
    [MEDIA_QUERIES.ON_DESKTOP]: {
      padding: `21px ${BASE_SPACING}px`,
    },
  },
  firstTab: {
    marginLeft: -9,
    [MEDIA_QUERIES.ON_DESKTOP]: {
      marginLeft: -BASE_SPACING,
    },
  },
  wrapper: {
    display: 'flex',
    height: 60,
    alignItems: 'center',
  },
  active: {
    color: LOCHMARA,
  },
  underline: {
    ':after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 9,
      right: 9,
      height: 4,
      backgroundColor: LOCHMARA,
      [MEDIA_QUERIES.ON_DESKTOP]: {
        left: BASE_SPACING,
        right: BASE_SPACING,
      },
    },
  },
});

export const createId = (messageId) =>
  `#${getTranslation(messageId).toLowerCase().replace(/\s+/g, '-')}`;

const getActiveTabIndex = (tabs) => (state) => {
  const id = getActiveTab(state);

  return Math.max(
    tabs.findIndex((tab) => id === createId(tab.messageId)),
    0
  );
};

const Tab = ({ tabs, children }) => {
  const active = useSelector(getActiveTabIndex(tabs));
  const [underlineIndex, setUnderline] = useState(active);

  useEffect(() => {
    setUnderline(active);
  }, [active]);

  return (
    <>
      <nav className={css(s.root)}>
        <Wrapper __className={s.wrapper}>
          {tabs.map((tab, index) => {
            return (
              <Button
                {...tab}
                key={tab.messageId}
                __type={TYPES.TRANSPARENT}
                onMouseEnter={() => !tab.disabled && setUnderline(index)}
                onMouseLeave={() => !tab.disabled && setUnderline(active)}
                onTouchStart={(e) => {
                  e.target.click();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  const id = createId(tab.messageId);
                  if (!tab.disabled && active !== index) {
                    navigateTo(`${window.location.pathname}${id}`, {
                      method: 'replace',
                    });
                    e.preventDefault();
                  }
                }}
                __className={[
                  s.tab,
                  !index && s.firstTab,
                  active === index && s.active,
                  underlineIndex === index && s.underline,
                ]}
              />
            );
          })}
        </Wrapper>
      </nav>
      {React.Children.toArray(children)[active]}
    </>
  );
};

Tab.propTypes = {
  tabs: PropTypes.array,
  children: PropTypes.any,
};

export default Tab;
